//#region IMPORTS
import {
  fetchInstoreTemplateAsync,
  instoreTemplateRowSelectors,
} from "../instoreTemplates/instoreTemplatesSlice";
import {
  instoreTypesSelectors,
  fetchInstoreTypesAsync,
} from "../instoreTypes/instoreTypesSlice";
import {
  clearInstoreBasket,
  addInstoreBasketItem,
  setInstoreBasketStore,
} from "../instoreBasket/instoreBasketSlice";
import { SEARCH_STORE_MODAL } from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { InstoreTemplateRow } from "../../models/instoreTemplate";
import { useTranslation } from "react-i18next";
import { openModal } from "../dashBoard/dashboardSlice";
import { Store } from "../../models/store";
import { toast } from "react-toastify";
import InstoreOrderSearchStoreForm from "../../components/instoreOrderDetail/InstoreOrderSearchStoreForm";
import WeekOrderItemsList from "../../components/weekorder/WeekOrderItemsList";
import useActiveWeekInstoreTemplates from "../../hooks/useActiveWeekInstoreTemplates";
import resolveWeekTemplateForStoreFormat from "../../helpers/resolveWeekTemplateForStoreFormat";
import dateHelperWeirdFromBackend from "../../helpers/dateHelperWeirdFromBackend";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../../components/_layout/MainContainer";
import useBasketGate from "../../hooks/useBasketGate";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../../components/genericPageHeader/PageHeader";
import MainModal from "../../components/_layout/MainModal";
//#endregion

export default function WeekOrderPage() {
  //#region SETUP
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const allRows = useAppSelector(instoreTemplateRowSelectors.selectAll);
  const { lastLoadedTemplateId, loading } = useAppSelector(
    (state) => state.instoreTemplates,
  );
  const { instoreTypes_Loaded } = useAppSelector((state) => state.instoreTypes);
  const instoreTypes = useAppSelector(instoreTypesSelectors.selectAll);
  const { user } = useAppSelector((state) => state.account);
  const { items: instoreBasketItems, store: impersonatedStore } =
    useAppSelector((state) => state.instoreBasket);

  // Roles — HQ, Picker and Admin all order on behalf of an impersonated store.
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);
  const isPicker = usePickerManager(user);
  const isStoreImpersonator = isHq || isPicker || isAdmin;

  // The store that drives the order: an impersonated store for staff, the
  // user's own store otherwise.
  const orderStore = isStoreImpersonator
    ? impersonatedStore ?? null
    : user?.store ?? null;

  // The chosen store's format decides the template: a store format belongs to
  // one instore type, and there is (normally) one Week template per type. So
  // picking the store *is* picking the template — no separate template picker.
  const weekTemplates = useActiveWeekInstoreTemplates();
  const template = resolveWeekTemplateForStoreFormat(
    weekTemplates,
    instoreTypes,
    orderStore?.store_format_id,
  );
  const effectiveTemplateId = template?.id ?? null;

  // Effective store format filter
  const effectiveStoreFormatId = orderStore?.store_format_id ?? null;

  // Only trust the loaded rows once they belong to the resolved template
  // (rows for a previous store/template may still linger in the store).
  const rowsLoaded = lastLoadedTemplateId === effectiveTemplateId;
  const rows = useMemo(() => {
    if (!rowsLoaded) return [];
    return effectiveStoreFormatId
      ? allRows.filter((row) => row.store_format_id === effectiveStoreFormatId)
      : allRows;
  }, [rowsLoaded, allRows, effectiveStoreFormatId]);

  // Quantity per template_row_id currently in the instore basket
  const quantityByRowId = useMemo(() => {
    const map = new Map<number, number>();
    instoreBasketItems.forEach((item) =>
      map.set(item.template_row_id, item.quantity),
    );
    return map;
  }, [instoreBasketItems]);

  // Group rows by theme, sort by `index` within and between groups
  const groups = useMemo(() => {
    const map = new Map<
      number | string,
      { theme_id: number; theme: string; items: InstoreTemplateRow[] }
    >();
    for (const row of rows) {
      const key = row.theme_id ?? row.theme ?? `row_${row.id}`;
      if (!map.has(key)) {
        map.set(key, { theme_id: row.theme_id, theme: row.theme, items: [] });
      }
      map.get(key)!.items.push(row);
    }
    const list = Array.from(map.values());
    list.forEach((g) =>
      g.items.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    );
    list.sort((a, b) => (a.items[0]?.index ?? 0) - (b.items[0]?.index ?? 0));
    return list;
  }, [rows]);

  const { guardInstoreAction } = useBasketGate();

  // Privileged users (HQ / Picker / Admin) order on behalf of an impersonated
  // store; ordering is blocked until they pick one.
  const orderingDisabled = isStoreImpersonator && !impersonatedStore;
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (!instoreTypes_Loaded) dispatch(fetchInstoreTypesAsync());
  }, [instoreTypes_Loaded, dispatch]);

  useEffect(() => {
    if (
      effectiveTemplateId != null &&
      effectiveTemplateId !== lastLoadedTemplateId
    ) {
      dispatch(fetchInstoreTemplateAsync(effectiveTemplateId));
    }
  }, [effectiveTemplateId, lastLoadedTemplateId, dispatch]);

  useEffect(() => {
    if (isStoreImpersonator && !impersonatedStore) {
      dispatch(openModal(SEARCH_STORE_MODAL));
    }
  }, [isStoreImpersonator, impersonatedStore, dispatch]);

  const addRowToBasket = guardInstoreAction(
    (row: InstoreTemplateRow, quantity: number) => {
      if (!template) return;
      dispatch(
        addInstoreBasketItem({ template_id: template.id, row, quantity }),
      );
    },
  );

  // Store search modal handlers
  const openStoreSearch = () => {
    if (instoreBasketItems.length > 0) {
      toast.error(
        "Clear the basket before switching to another store."
      );
      return;
    }
    dispatch(openModal(SEARCH_STORE_MODAL));
  };

  const handleStorePicked = (store: Store) => {
    if (!store) {
      toast.error("No stores found");
      return;
    }
    // The store's format must have an active Week template (format → instore
    // type → Week template). That template is what the order is built from.
    const matching = resolveWeekTemplateForStoreFormat(
      weekTemplates,
      instoreTypes,
      store.store_format_id,
    );
    if (!matching) {
      toast.error("No active week order available for this store's format.");
      return;
    }
    dispatch(setInstoreBasketStore(store));
    dispatch(openModal(false));
    // Non-blocking warning when ordering for a store that is not open yet.
    // `active` comes back as 1/0 from the backend, so compare numerically.
    if (Number(store.active) === 0) {
      toast.warning(
        "Heads up: this store is not open yet. You can still place the order.",
      );
    }
  };

  const clearImpersonation = () => {
    if (instoreBasketItems.length > 0) {
      toast.error("Clear the basket before clearing the impersonation.");
      return;
    }
    dispatch(setInstoreBasketStore(null));
  };

  const clearBasket = () => {
    dispatch(clearInstoreBasket());
    toast.success("Your instore basket is empty");
  };
  //#endregion

  //#region RENDER
  return (
    <MainContainer>
      <MainModal
        type={SEARCH_STORE_MODAL}
        closeDisabled={isStoreImpersonator && !impersonatedStore}
        closeHandler={() => dispatch(openModal(false))}
      >
        <InstoreOrderSearchStoreForm
          setStoreHandler={handleStorePicked}
          cancel={() => {
            dispatch(openModal(false));
            if (isStoreImpersonator && !impersonatedStore) {
              history.goBack();
            }
          }}
        />
      </MainModal>

      <PageHeader title={t("rituals.weekOrder") ?? "Week order"} />

      {template && (
        <div className="text-right">
          <p>
            <b>{template.name}</b>
          </p>
        </div>
      )}

      {/* HQ / Picker / Admin: store impersonation */}
      {isStoreImpersonator && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <div className="text-xl font-bold uppercase tracking-wide text-gray-800">
            {impersonatedStore?.store_format?.name}
          </div>
          <div className="flex items-center gap-3">
          {impersonatedStore ? (
            <div className="inline-flex items-center gap-2 rounded-md bg-purple-100 text-purple-900 px-3 py-1.5">
              <span className="font-medium">
                Ordering for {impersonatedStore.external_store_id} —{" "}
                {impersonatedStore.store_name}
              </span>
              <button
                type="button"
                onClick={clearImpersonation}
                disabled={instoreBasketItems.length > 0}
                className="text-purple-700 hover:text-purple-500 font-semibold disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-purple-700"
              >
                clear
              </button>
            </div>
          ) : (
            <p className="text-gray-500">No store selected</p>
          )}
          <button
            type="button"
            onClick={openStoreSearch}
            disabled={instoreBasketItems.length > 0}
            className="rounded-md bg-pink-700 text-white px-3 py-1.5 font-semibold hover:bg-pink-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-pink-700"
          >
            {impersonatedStore ? "Change store" : "Pick a store"}
          </button>
          {instoreBasketItems.length > 0 && (
            <button
              type="button"
              onClick={clearBasket}
              className="rounded-md bg-white text-red-700 ring-1 ring-inset ring-red-300 px-3 py-1.5 font-semibold hover:bg-red-50"
            >
              Clear basket
            </button>
          )}
          </div>
        </div>
      )}

      {/* Non-blocking warning: ordering for a store that is not open yet. */}
      {isStoreImpersonator &&
        impersonatedStore &&
        Number(impersonatedStore.active) === 0 && (
        <div className="mt-3 rounded-md bg-amber-100 px-4 py-2 text-sm font-medium text-amber-900">
          This store is not open yet — you can still place the order.{" "}
          <span className="font-bold">
            {impersonatedStore.opening_date
              ? `Opening date: ${dateHelperWeirdFromBackend(
                  impersonatedStore.opening_date,
                )}`
              : "Opening date not confirmed."}
          </span>
        </div>
      )}

      {orderStore && !template && !loading && (
        <p className="mt-6 text-gray-500">
          {t("rituals.noActiveWeekOrder")}
        </p>
      )}

      <WeekOrderItemsList
        groups={groups}
        quantityByRowId={quantityByRowId}
        onRowAdd={addRowToBasket}
        loading={loading}
        disabled={orderingDisabled}
        // Store format is already shown once in the header for every role, so
        // don't repeat it under each product name.
        showStoreFormat={false}
      />
    </MainContainer>
  );
  //#endregion
}
